import _ from "lodash";
import React, { useEffect } from "react";
import { Button, IconButton, DSProvider, Tooltip } from "@toddle-edu/ds-web";
import { DeleteOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import {
  mapStateToProps,
  mapActionCreators,
  isInvalidEventForm,
  getEventDetails,
  getIsRecurringEventSettingsUpdated,
  setValue,
  isNewVideoMeetingUIFeatureEnabled,
} from "../../modules/Utils";
import { connect } from "react-redux";
import EventUpdateConfirmationModal from "../EventUpdateConfirmationModal";
import RecurrenceConfirmationModal from "../RecurrenceConfirmationModal";
import {
  createSchoolCalendarEvent,
  editSchoolCalendarEvent,
  deleteSchoolCalendarEvent,
} from "MyCalendar/modules/MyCalendarModule";
import useDeleteRecurrance from "../../hooks/useDeleteRecurrance";
import {
  useIsEventFormUpdated,
  useIsOnlyRemindersUpdated,
  useUpdateRecurrance,
} from "../../hooks";
import { getDateMomentObject } from "Utils/DateTimeHelpers";

const localMapActionCreators = {
  ...mapActionCreators,
  createSchoolCalendarEvent,
  editSchoolCalendarEvent,
  deleteSchoolCalendarEvent,
};

const CreateButton = ({
  onClick,
  testId = "create-event-button",
  eventModalV2,
  label = null,
  createSchoolCalendarEvent,
  setShowFullScreenLoader,
  updateEventModalV2,
}) => {
  const { t } = useI18n(["common", "calendar"]);
  const isFormInvalid = isInvalidEventForm(eventModalV2);
  const isFormUpdated = useIsEventFormUpdated();
  const setIsEventDetailsFormUpdated = setValue(
    "isFormUpdated",
    updateEventModalV2
  );
  const createLabel = isNewVideoMeetingUIFeatureEnabled()
    ? "common:create"
    : "calendar:create_event";
  useEffect(() => {
    setIsEventDetailsFormUpdated(isFormUpdated);
  }, [isFormUpdated]);

  const clickHandler = async () => {
    const eventDetails = getEventDetails(eventModalV2);
    try {
      setShowFullScreenLoader(true);
      await createSchoolCalendarEvent(eventDetails);
      if (_.isFunction(onClick)) {
        onClick();
      }
    } catch (error) {
      console.error("Error creating event:", error);
    }
    setShowFullScreenLoader(false);
  };
  const buttonJSX = (
    <Button
      variant="primary"
      onClick={clickHandler}
      testId={testId}
      disabled={isFormInvalid}
    >
      {label ? label : t(createLabel)}
    </Button>
  );

  return isFormInvalid ? (
    <Tooltip
      dsVersion="2.0"
      tooltip={t("calendar:fill_mandatory_fields")}
      placement="bottomLeft"
      showArrow={true}
    >
      {buttonJSX}
    </Tooltip>
  ) : (
    buttonJSX
  );
};

export const CreateEventButton = connect(
  mapStateToProps,
  localMapActionCreators
)(CreateButton);

const UpdateButton = ({
  onClick,
  testId = "update-event-button",
  eventModalV2,
  label = null,
  editSchoolCalendarEvent,
  setShowFullScreenLoader,
  updateEventModalV2,
}) => {
  const isFormInvalid = isInvalidEventForm(eventModalV2);
  const {
    clickHandler,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
    onSaveRecurrenceHandler,
    repeatEventModalActionRef,
    existingLabelText,
    updatedLabelText,
    showNewConfirmationModal,
    t,
    onCloseConfirmationModal,
    isRemoveRepeatRecurranceSettings,
  } = useUpdateRecurrance({ updateEventModalV2, eventModalV2 });

  const {
    readOnlyExistingRepeatEventSettings,
    startDate,
    repeatEventSettings,
    readOnlyStartDate,
    readOnlyEndDate,
    endDate,
    isRepeatEvent,
    readOnlySkipNonOperationalDays,
    skipNonOperationalDays,
  } = eventModalV2 || {};
  const isOnlyRemindersUpdated = useIsOnlyRemindersUpdated();
  const isFormUpdated = useIsEventFormUpdated();
  const setIsEventDetailsFormUpdated = setValue(
    "isFormUpdated",
    updateEventModalV2
  );
  useEffect(() => {
    setIsEventDetailsFormUpdated(isFormUpdated);
  }, [isFormUpdated]);
  const isRecurringEventSettingsUpdated = isRepeatEvent
    ? getIsRecurringEventSettingsUpdated(
        readOnlyExistingRepeatEventSettings,
        repeatEventSettings,
        startDate
      )
    : false;
  const isSkipNonOperationalDaysUpdated = isRepeatEvent
    ? readOnlySkipNonOperationalDays !== skipNonOperationalDays
    : false;
  const isDateChanged =
    !_.isEqual(
      getDateMomentObject(readOnlyStartDate).format("YYYY-MM-DD"),
      getDateMomentObject(startDate).format("YYYY-MM-DD")
    ) ||
    !_.isEqual(
      getDateMomentObject(readOnlyEndDate).format("YYYY-MM-DD"),
      getDateMomentObject(endDate).format("YYYY-MM-DD")
    );

  const onConfirm = async data => {
    const { message } = data;
    const eventDetails = getEventDetails(eventModalV2, message);
    try {
      setShowFullScreenLoader(true);
      await editSchoolCalendarEvent(eventDetails);
      if (_.isFunction(onClick)) {
        onClick(data);
      }
    } catch (error) {
      console.error("Error creating event:", error);
    }
    setIsConfirmationModalOpen(false);
    setShowFullScreenLoader(false);
  };

  return (
    <>
      <EventUpdateConfirmationModal
        isOpen={isConfirmationModalOpen}
        onConfirm={onConfirm}
        onClose={onCloseConfirmationModal}
        height="max-content"
        showWarning={!showNewConfirmationModal}
        saveButtonLabel="common:update"
        messagePlaceholder="calendar:add_optional_message"
        showMessageLabel={!showNewConfirmationModal}
        showCheckbox={!isOnlyRemindersUpdated}
        showMessageInput={!isOnlyRemindersUpdated}
      />

      <RecurrenceConfirmationModal
        actionRef={repeatEventModalActionRef}
        isOpen={false}
        eventTitle={eventModalV2?.eventTitle || ""}
        existingLabelText={existingLabelText}
        updatedLabelText={
          isRemoveRepeatRecurranceSettings
            ? t("calendar:event_does_not_repeat")
            : updatedLabelText
        }
        title={t("calendar:edit_repeat_event")}
        showRepeatInfo={!_.isEqual(existingLabelText, updatedLabelText)}
        onSave={onSaveRecurrenceHandler}
        onClose={onCloseConfirmationModal}
        isRemoveRepeatRecurranceSettings={isRemoveRepeatRecurranceSettings}
        isDateChanged={isDateChanged}
        isRecurringEventSettingsUpdated={
          isRecurringEventSettingsUpdated || isSkipNonOperationalDaysUpdated
        }
      />
      <Button
        variant="primary"
        onClick={clickHandler}
        testId={testId}
        disabled={isFormInvalid || !isFormUpdated}
      >
        {label ? label : t("common:save")}
      </Button>
    </>
  );
};

export const UpdateEventButton = connect(
  mapStateToProps,
  localMapActionCreators
)(UpdateButton);

const CancelButton = ({
  onClick,
  testId = "cancel-event-button",
  label = null,
}) => {
  const { t } = useI18n(["common", "calendar"]);
  const clickHandler = () => {
    // reset the state of the event modal
    if (_.isFunction(onClick)) {
      onClick();
    }
  };

  return (
    <Button
      variant="neutral"
      type="outlined"
      onClick={clickHandler}
      testId={testId}
    >
      {label ? label : t("common:cancel")}
    </Button>
  );
};

export const CancelEventButton = connect(
  mapStateToProps,
  localMapActionCreators
)(CancelButton);

const DeleteButton = ({
  onClick,
  testId = "update-event-button",
  eventModalV2,
  style = {},
  deleteSchoolCalendarEvent,
  isFullDayEvent,
  id,
  onCloseEventDetailsModal,
  updateEventModalV2,
  isRepeatEvent,
}) => {
  const {
    clickHandler,
    isConfirmationModalOpen,
    onCloseConfirmationModal,
    onSaveRecurrenceHandler,
    repeatEventModalActionRef,
    setIsConfirmationModalOpen,
    t,
  } = useDeleteRecurrance({ updateEventModalV2, isRepeatEvent });

  const onConfirm = async data => {
    const { message, sendMessage } = data;
    try {
      await deleteSchoolCalendarEvent({
        id,
        isFullDayEvent,
        message,
        sendMessage,
      });
      if (_.isFunction(onClick)) {
        onClick(data);
      }
    } catch (error) {
      console.error("Error creating event:", error);
    }
    onCloseEventDetailsModal();
    setIsConfirmationModalOpen(false);
  };

  return (
    <DSProvider dsVersion="2.0">
      <EventUpdateConfirmationModal
        isOpen={isConfirmationModalOpen}
        onConfirm={onConfirm}
        onClose={onCloseConfirmationModal}
        title="calendar:delete_event"
        checkboxLabel="calendar:notify_all_the_participants_about_the_deletion_of_this_event"
        confirmationMessage="calendar:delete_confirmation_message"
        saveButtonLabel="common:update"
        cancelButtonLabel="common:cancel"
        height="max-content"
        showWarning={false}
      />
      <RecurrenceConfirmationModal
        actionRef={repeatEventModalActionRef}
        isOpen={false}
        eventTitle={eventModalV2?.eventTitle || ""}
        title={t("calendar:edit_repeat_event")}
        onSave={onSaveRecurrenceHandler}
        onClose={onCloseConfirmationModal}
      />
      <IconButton
        icon={<DeleteOutlined variant={"subtle"} />}
        onClick={clickHandler}
        variant={"neutral"}
        type={"plain"}
        size={"medium"}
        isCompact={true}
        style={style}
        testId={`${testId}-header-delete`}
      />
    </DSProvider>
  );
};

export const DeleteEventButton = connect(
  mapStateToProps,
  localMapActionCreators
)(DeleteButton);
